import ExitCrossIcon from 'lib.assets/src/ExitCrossIcon';
import ChatIcon from 'lib.assets/src/ChatIcon';
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  isHubSpotScriptLoaded,
  isMobileDevice,
  toggleHubspotChat,
  toggleHubspotStyles,
  fetchHubspotIdentity,
  identifyAndLoadHubspotWidget,
  loadHubspotWidgetAnonymous,
} from './helpers';
import { useAuth } from 'lib.context/src/AuthContext';

const MAX_IDENTITY_RETRIES = 3;
const RETRY_DELAY_MS = 5000;

const HubSpotButton = () => {
  const [isHubspotReady, setIsHubspotReady] = useState(false);
  const [isWidgetVisible, setIsWidgetVisible] = useState(false);
  const [identityRetryCount, setIdentityRetryCount] = useState(0);
  const { isHubspotChatOpen, setIsHubspotChatOpen, user, isUserLoading } = useAuth();
  const isMobile = isMobileDevice();
  // Track which email was last identified (null = anonymous, string = identified email)
  const lastIdentifiedEmailRef = useRef<string | null | undefined>(undefined);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const toggleChat = useCallback(() => {
    const status = window.HubSpotConversations?.widget.status();

    if (!window.HubSpotConversations) {
      console.error('HubSpot Conversations not available');
      return;
    }

    if (!status?.loaded) {
      console.warn('HubSpot widget not loaded yet, attempting to load...');
      window.HubSpotConversations.widget.load();
      return;
    }

    const isOpenClass = document.querySelector('.hs-messages-widget-open');
    const isOpen = Boolean(isOpenClass || isHubspotChatOpen);
    toggleHubspotChat(isOpen);
    setIsHubspotChatOpen(!isOpen);
  }, [isHubspotChatOpen, setIsHubspotChatOpen]);

  const addHubspotCloseButton = useCallback(() => {
    const hubspotContainer = document.querySelector('#hubspot-messages-iframe-container');
    const hubspotClose = document.querySelector('.hubspot-close-button');
    if (hubspotContainer && !hubspotClose) {
      const newElement = document.createElement('div');
      newElement.addEventListener('click', () => {
        toggleChat();
      });
      newElement.className = 'hubspot-close-button';
      hubspotContainer.appendChild(newElement);
    }
  }, [toggleChat]);

  // Wait for HubSpot script to be ready
  useEffect(() => {
    const interval = setInterval(() => {
      if (isHubSpotScriptLoaded()) {
        clearInterval(interval);
        setIsHubspotReady(true);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Fetch identity and load widget when HubSpot is ready and user loading is complete
  // Re-runs when user logs in (email changes from null to string) or logs out
  useEffect(() => {
    if (!isHubspotReady || isUserLoading) {
      return;
    }

    const currentEmail = user?.email || null;

    // Skip if we already successfully processed this email state (prevents duplicate calls)
    if (lastIdentifiedEmailRef.current === currentEmail) {
      return;
    }

    // Clear any pending retry when email changes
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }

    const initializeWidget = async () => {
      if (currentEmail) {
        // Authenticated user - fetch identity token from backend
        const identity = await fetchHubspotIdentity();
        if (identity) {
          identifyAndLoadHubspotWidget(identity);
          // Only mark as identified after successful identification
          lastIdentifiedEmailRef.current = currentEmail;
          setIdentityRetryCount(0);
        } else {
          // Fallback to anonymous if identity fetch fails
          loadHubspotWidgetAnonymous();

          // Schedule retry if we haven't exceeded max retries
          if (identityRetryCount < MAX_IDENTITY_RETRIES) {
            retryTimeoutRef.current = setTimeout(() => {
              setIdentityRetryCount((prev) => prev + 1);
            }, RETRY_DELAY_MS);
          }
        }
      } else {
        // Anonymous user (logged out)
        loadHubspotWidgetAnonymous();
        lastIdentifiedEmailRef.current = null;
        setIdentityRetryCount(0);
      }
    };

    void initializeWidget();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [isHubspotReady, isUserLoading, user?.email, identityRetryCount]);

  // Setup widget UI after it loads (runs only once when widget becomes ready)
  useEffect(() => {
    if (!isHubspotReady || isWidgetVisible) {
      return;
    }

    const checkWidgetLoaded = setInterval(() => {
      const hubspotContainer = document.querySelector('#hubspot-messages-iframe-container');
      const status = window.HubSpotConversations?.widget.status();

      // Widget is ready when container exists and status shows loaded
      if (hubspotContainer && status?.loaded) {
        clearInterval(checkWidgetLoaded);
        addHubspotCloseButton();
        // Close widget initially (user will open it by clicking Help button)
        window.HubSpotConversations?.widget.close();
        toggleHubspotStyles(true);
        setIsHubspotChatOpen(false);
        setIsWidgetVisible(true);
      }
    }, 200);

    // Timeout fallback - if widget doesn't load in 10s, show button anyway
    const timeout = setTimeout(() => {
      clearInterval(checkWidgetLoaded);
      if (window.HubSpotConversations) {
        setIsWidgetVisible(true);
      }
    }, 10000);

    return () => {
      clearInterval(checkWidgetLoaded);
      clearTimeout(timeout);
    };
  }, [isHubspotReady, isWidgetVisible, addHubspotCloseButton, setIsHubspotChatOpen]);

  return isWidgetVisible ? (
    <button
      className={`hubspot-chat-button ${isMobile ? 'hubspot-chat-button__mobile' : ''}`}
      onClick={toggleChat}
    >
      <span className="hubspot-chat-text">Help</span>
      {isHubspotChatOpen ? <ExitCrossIcon /> : <ChatIcon />}
    </button>
  ) : null;
};

export default HubSpotButton;
